import { ProductData, ModelData } from '@ikea-aoa/component-view-3d';
import { AssetsPackage } from '@ikea-aoa/elements-3d';

const DEV_HOSTING = '/addon-app/sofas/content/test3dAssets';

export class AssetsUrls {
	public static getModelUrls(
		folderName: string | null,
		assetsUrl: string,
		products: ProductData,
		models: ModelData,
		modelSwitchMap: { [key: string]: { [key: string]: string } } | null
	): Map<string, string[]> {
		const allUrls: Map<string, string[]> = new Map<string, string[]>();
		for (const key in products) {
			const product = products[key];
			const color = product.filter.color;
			if (color) {
				const url = this.createUrlForAssetId(
					folderName,
					assetsUrl,
					product.modelId,
					modelSwitchMap,
					color
				);
				if (allUrls.has(color)) {
					(allUrls.get(color) as string[]).push(url);
				} else {
					allUrls.set(color, [url]);
				}

				// create URLs for hasFeature models
				const model = models[product.modelId];
				const hasFeature = model && model.logic && model.logic.hasFeature;
				if (hasFeature) {
					const url = this.createUrlForAssetId(
						folderName,
						assetsUrl,
						product.modelId,
						modelSwitchMap,
						color,
						hasFeature
					);
					if (allUrls.has(color)) {
						(allUrls.get(color) as string[]).push(url);
					} else {
						allUrls.set(color, [url]);
					}
				}
			}
		}
		return allUrls;
	}

	public static createUrlForAssetId(
		folderName: string | null,
		assetsUrl: string,
		modelId: string,
		modelSwitchMap: { [key: string]: { [key: string]: string } } | null,
		color?: string,
		hasFeature = false,
		format = 'gltf'
	): string {
		const cover = color ? '_' + color : '';

		if (modelSwitchMap && color && modelSwitchMap[color]) {
			if (modelSwitchMap[color][modelId]) {
				modelId = modelSwitchMap[color][modelId];
			}
		}

		if (folderName) {
			return `${DEV_HOSTING}/${folderName}/${modelId}${cover}${
				hasFeature ? '_FEATURE' : ''
			}.${format}`;
		}

		return `${assetsUrl}/${modelId}${cover}${hasFeature ? '_FEATURE' : ''}.${format}`;
	}

	public static getUrlPackage(
		folderName: string | null,
		assetsUrl: string,
		products: ProductData,
		models: ModelData,
		path: string,
		modelSwitchMap: { [key: string]: { [key: string]: string } } | null
	): AssetsPackage {
		const baseUrl = folderName ? `${DEV_HOSTING}/${folderName}` : `${assetsUrl}/${path}`;
		const materialsUrl = `${baseUrl}/materials/materials_lib.gltf`;
		const geometrySet = new Set<string>();

		const map = new Map<string, { geometry: string; material: string }>();
		for (const id in products) {
			const product = products[id];
			const { color } = product.filter;
			let { modelId } = product;
			const { key, geometry, material } = this.createUrlPackageItem(
				folderName,
				assetsUrl,
				product.modelId,
				modelSwitchMap,
				color
			);
			map.set(key, { geometry, material });

			if (modelSwitchMap && color && modelSwitchMap[color]) {
				if (modelSwitchMap[color][modelId]) {
					modelId = modelSwitchMap[color][modelId];
				}
			}

			// getting geometry url
			const geometryUrl = `${baseUrl}/geometry/${modelId}.glb`;
			geometrySet.add(geometryUrl);

			// create package for hasFeature models
			const model = models[product.modelId];
			const hasFeature = model && model.logic && model.logic.hasFeature;
			if (hasFeature) {
				const { key, geometry, material } = this.createUrlPackageItem(
					folderName,
					assetsUrl,
					product.modelId,
					modelSwitchMap,
					color,
					true
				);
				map.set(key, { geometry, material });

				// getting geometry url
				const geometryUrl = `${baseUrl}/geometry/${modelId}${hasFeature ? '_FEATURE' : ''}.glb`;
				geometrySet.add(geometryUrl);
			}
		}

		const geometryUrls = Array.from(geometrySet);
		return { materialsUrl, geometryUrls, map };
	}

	public static getEnvironmentalTextureUrl(assetsUrl: string, name: string): string {
		return assetsUrl + '/' + name + '.env';
	}

	private static createUrlPackageItem(
		folderName: string | null,
		assetsUrl: string,
		modelId: string,
		modelSwitchMap: { [key: string]: { [key: string]: string } } | null,
		color?: string,
		hasFeature = false,
		format = 'gltf'
	): { key: string; geometry: string; material: string } {
		const cover = color ? '_' + color : '';

		if (modelSwitchMap && color && modelSwitchMap[color]) {
			if (modelSwitchMap[color][modelId]) {
				modelId = modelSwitchMap[color][modelId];
			}
		}

		const geometry = hasFeature ? modelId + '_FEATURE' : modelId;
		const material = color ? modelId + cover + (hasFeature ? '_FEATURE' : '') : '';

		const key = this.createUrlForAssetId(
			folderName,
			assetsUrl,
			modelId,
			modelSwitchMap,
			color,
			hasFeature,
			format
		);
		return {
			key,
			geometry,
			material,
		};
	}
}
